import FileUploadLine from "./FileUploadLine";
import RelevesAvailable from "./RelevesAvailable";
import SecuriteSocialeImportsAvailable from "./SecuriteSocialeImportsAvailable";
import _ from "underts";

type PreRapprochementSummary = {
  dryRun: boolean;
  attemptedGroups: number;
  matchedGroups: number;
  linkedLogLines: number;
  skippedGroups: { groupKey: string; reason: string }[];
  error?: string;
};

type SecuriteSocialeImportResult = {
  detectedEncoding: string;
  sourceFileHash: string;
  importId: string | null;
  insertedCount: number;
  skippedBecauseDuplicate: boolean;
  totalAmountCent: number;
  dryRun: boolean;
  preRapprochement: PreRapprochementSummary;
};

type UploadFileResult = {
  fileName: string;
  kind: "securiteSocialeCsv" | "pdf" | "excel" | "unknown";
  success: boolean;
  message?: string;
  securiteSocialeImport?: SecuriteSocialeImportResult;
};

type UploadApiResponse = {
  success: boolean;
  fileResults: UploadFileResult[];
  globalError?: string;
};

interface FileUploadProps {
  uploadState: {
    uploadState: "started" | "stopped" | "error" | "success";
    currentFiles: string[];
    progress: {
      [fileName: string]: number;
    };
    filesWithError: string[];
    completedFiles: string[];
    order: {
      [fileName: string]: number;
    };
  };
  startUpload: (files: File[], options?: { dryRun?: boolean }) => void;
  dryRun: boolean;
  onDryRunChange: (value: boolean) => void;
  currentYear: number;
  lastUploadResponse: UploadApiResponse | null;
}

const FileUpload = (props: FileUploadProps) => {
  const { uploadState, startUpload } = props;
  console.log("uploadState: ", uploadState);
  //TODO tous ces traitements devraient probablement être faits dans un selector
  //TODO les fichiers peuvent être à la fois dans success et error trouver solution
  const filesComp = uploadState.currentFiles
    .sort((a, b) => uploadState.order[a] - uploadState.order[b])
    .map((fileName, idx) => (
      <FileUploadLine
        key={"FileUploadLine" + idx}
        fileName={fileName}
        progress={uploadState.progress[fileName]}
        hasError={uploadState.filesWithError.indexOf(fileName) !== -1}
        hasSuccess={uploadState.completedFiles.indexOf(fileName) !== -1}
      />
    ));
  const completedFilesComps = uploadState.completedFiles.map((fileName) => (
    <FileUploadLine
      key={"CompletedFileKey" + fileName}
      fileName={fileName}
      progress={100}
      hasError={false}
      hasSuccess={true}
    />
  ));
  const filesWithErrorComps = uploadState.filesWithError.map((fileName) => (
    <FileUploadLine
      key={"errorFileKey" + fileName}
      fileName={fileName}
      progress={100}
      hasError={true}
      hasSuccess={false}
    />
  ));

  const securiteSocialeResults = (props.lastUploadResponse?.fileResults || [])
    .filter((result) => result.kind === "securiteSocialeCsv")
    .map((result) => ({
      ...result,
      securiteSocialeImport: result.securiteSocialeImport,
    }));

  const failedResults = (props.lastUploadResponse?.fileResults || []).filter(
    (result) => !result.success,
  );

  const uploadErrorsComp =
    props.lastUploadResponse &&
    (!props.lastUploadResponse.success ||
      Boolean(props.lastUploadResponse.globalError)) ? (
      <div
        style={{
          marginTop: "12px",
          padding: "12px",
          border: "1px solid #f2c6c6",
          borderRadius: "8px",
          background: "#fff6f6",
          width: "600px",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: "8px", color: "#a31515" }}>
          Erreurs d'upload
        </div>
        {props.lastUploadResponse.globalError ? (
          <div>{props.lastUploadResponse.globalError}</div>
        ) : null}
        {failedResults.map((result) => (
          <div key={`upload-error-${result.fileName}-${result.kind}`}>
            {result.fileName}: {result.message || "Erreur inconnue"}
          </div>
        ))}
      </div>
    ) : null;

  const preRapprochementComp =
    securiteSocialeResults.length > 0 ? (
      <div
        style={{
          marginTop: "12px",
          padding: "12px",
          border: "1px solid #d9e6f2",
          borderRadius: "8px",
          background: "#f8fbff",
          width: "600px",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: "8px" }}>
          Import Sécu (rapprochement différé)
        </div>
        {securiteSocialeResults.map((result) => {
          const importResult = result.securiteSocialeImport;
          const pre = importResult?.preRapprochement;
          const totalEuros = importResult
            ? (importResult.totalAmountCent / 100).toFixed(2)
            : "0.00";
          const isDryRun = importResult?.dryRun || false;
          const modeLabel = isDryRun
            ? "Mode: simulation (dry run)"
            : "Mode: import enregistré";
          const linesLabel = isDryRun ? "Lignes simulées" : "Lignes importées";
          const hasPreDetails =
            !!pre &&
            (pre.attemptedGroups > 0 ||
              pre.matchedGroups > 0 ||
              pre.linkedLogLines > 0 ||
              pre.skippedGroups.length > 0 ||
              !!pre.error);
          return (
            <div
              key={`secu-import-${result.fileName}`}
              style={{
                borderTop: "1px solid #e4eef8",
                paddingTop: "8px",
                marginTop: "8px",
              }}
            >
              <div style={{ fontWeight: 600 }}>{result.fileName}</div>
              {result.message ? <div>{result.message}</div> : null}
              {importResult ? (
                <div>
                  <div>{modeLabel}</div>
                  <div>
                    {linesLabel}: {importResult.insertedCount} | Total:{" "}
                    {totalEuros} EUR | Encodage: {importResult.detectedEncoding}
                  </div>
                  {hasPreDetails && pre ? (
                    <div>
                      <div>
                        Groupes tentés: {pre.attemptedGroups} | Groupes
                        rapprochés: {pre.matchedGroups} | Lignes liées:{" "}
                        {pre.linkedLogLines}
                      </div>
                      {pre.error ? <div>Erreur: {pre.error}</div> : null}
                      {pre.skippedGroups.length > 0 ? (
                        <div>
                          Groupes ignorés:
                          {pre.skippedGroups.slice(0, 5).map((skip) => (
                            <div key={`${result.fileName}-${skip.groupKey}`}>
                              - {skip.groupKey}: {skip.reason}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    ) : null;

  return (
    <div>
      <label
        htmlFor="upload-dry-run"
        style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
      >
        <input
          id="upload-dry-run"
          type="checkbox"
          checked={props.dryRun}
          onChange={(event) => {
            props.onDryRunChange(event.currentTarget.checked);
          }}
        />
        Dry run (simulation sans enregistrement)
      </label>
      <input
        id="fileInput"
        multiple
        type="file"
        onChange={(event) => {
          if (event?.currentTarget?.files) {
            startUpload([].slice.call(event.currentTarget.files), {
              dryRun: props.dryRun,
            });
          }
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", width: "600px" }}>
        {filesComp}
        {completedFilesComps}
        {filesWithErrorComps}
      </div>
      {uploadErrorsComp}
      {preRapprochementComp}
      <div>
        <RelevesAvailable
          years={_.range(props.currentYear - 5, props.currentYear + 1)}
        />
        <SecuriteSocialeImportsAvailable
          years={_.range(props.currentYear - 5, props.currentYear + 1)}
        />
      </div>
    </div>
  );
};

export default FileUpload;
